import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react'
import { ChevronDown, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SelectOption } from './Select'
import { BottomSheet } from './BottomSheet'
import { Badge } from './Badge'

export interface SearchableDropdownProps {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void

  label?: string
  error?: string
  hint?: string
  placeholder?: string
  disabled?: boolean
  name?: string

  onSearch?: (query: string) => void
  isLoading?: boolean

  onLoadMore?: () => void
  hasMore?: boolean
  required?: boolean
}

export function SearchableDropdown({
  options,
  value,
  onChange,
  label,
  error,
  hint,
  placeholder = 'Select...',
  disabled = false,
  name,
  onSearch,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  required,
}: SearchableDropdownProps) {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const isEmpty = !value || value === '0' || value === 'undefined' || value === 'NaN'
  const selectedOption = options.find((o) => o.value === value)

  // Filter options: local mode filters by label, API mode shows all
  const filteredOptions = onSearch
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(searchText.toLowerCase()))

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li[data-option]')
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const openDropdown = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    setSearchText('')
    setHighlightedIndex(-1)
    if (onSearch) onSearch('')
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [disabled, onSearch])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setSearchText('')
  }, [])

  const selectOption = useCallback(
    (opt: SelectOption) => {
      onChange(opt.value)
      closeDropdown()
    },
    [onChange, closeDropdown]
  )

  // Mobile: the trigger opens an in-app bottom sheet (search box + options)
  // instead of the inline absolute dropdown, mirroring Select.tsx's MUI-2 pattern
  // and reusing the same search/filter state. Pure Tailwind breakpoint switch
  // (sm:hidden / hidden sm:block) — the desktop trigger drives `isOpen`, the
  // mobile trigger drives `sheetOpen`, so exactly one is interactive per breakpoint.
  const openSheet = useCallback(() => {
    if (disabled) return
    setSheetOpen(true)
    setSearchText('')
    if (onSearch) onSearch('')
  }, [disabled, onSearch])
  const closeSheet = useCallback(() => {
    setSheetOpen(false)
    setSearchText('')
  }, [])
  const selectSheetOption = useCallback(
    (opt: SelectOption) => {
      onChange(opt.value)
      closeSheet()
    },
    [onChange, closeSheet]
  )

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchText(query)
      setHighlightedIndex(-1)
      if (onSearch) {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => onSearch(query), 300)
      }
    },
    [onSearch]
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openDropdown()
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
          break
        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            selectOption(filteredOptions[highlightedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          closeDropdown()
          break
      }
    },
    [isOpen, openDropdown, closeDropdown, selectOption, filteredOptions, highlightedIndex]
  )

  // Infinite scroll: load more when scrolled near bottom
  const handleScroll = useCallback(() => {
    if (!onLoadMore || !hasMore || isLoading || !listRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 40) {
      onLoadMore()
    }
  }, [onLoadMore, hasMore, isLoading])

  return (
    <div className="form-control w-full" ref={containerRef}>
      {label && (
        <label className="label whitespace-normal" htmlFor={name}>
          <span className="label-text">
            {label}
            {required && <span className="text-error ml-0.5" aria-hidden="true">*</span>}
          </span>
        </label>
      )}

      {/* Desktop: inline trigger + absolute dropdown (unchanged). Hidden on mobile. */}
      <div className="hidden sm:block relative">
        {/* Trigger / Input */}
        {isOpen ? (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              className={`input input-bordered w-full pr-10 ${error ? 'input-error' : ''}`}
              placeholder={placeholder}
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
            />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/40 rotate-180 transition-transform" />
          </div>
        ) : (
          <button
            type="button"
            id={name}
            className={`input input-bordered w-full pr-10 text-left flex items-center ${error ? 'input-error' : ''} ${disabled ? 'bg-base-200 cursor-not-allowed' : 'cursor-pointer'} ${isEmpty ? 'text-base-content/40' : ''}`}
            onClick={openDropdown}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          >
            <span className="truncate leading-none">
              {isEmpty ? placeholder : selectedOption?.label || placeholder}
            </span>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/40" />
          </button>
        )}

        {/* Dropdown */}
        {isOpen && (
          <ul
            ref={listRef}
            className="absolute z-50 mt-1 w-full rounded-lg border border-base-300 bg-base-100 shadow-lg max-h-80 overflow-auto"
            onScroll={handleScroll}
          >
            {filteredOptions.map((opt, index) => (
              <li
                key={opt.value}
                data-option
                className={`px-4 py-2 cursor-pointer transition-colors ${
                  index === highlightedIndex ? 'bg-primary/10 text-primary' : ''
                } ${opt.value === value ? 'font-semibold' : ''} hover:bg-primary/10`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectOption(opt)
                }}
              >
                {/* opt.label stays a direct child (not span-wrapped) so existing
                    getByText(label) assertions across other pickers (which never
                    set `chip`/`description`) keep matching a single element,
                    unchanged. The two-line layout only kicks in when a caller
                    opts into `description`. */}
                {opt.description ? (
                  <div className="min-w-0">
                    <div className="flex items-center">
                      {opt.label}
                      {opt.chip && <Badge size="sm" variant="ghost" className="ml-2">{opt.chip}</Badge>}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-base-content/60">{opt.description}</div>
                  </div>
                ) : (
                  <>
                    {opt.label}
                    {opt.chip && <Badge size="sm" variant="ghost" className="ml-2">{opt.chip}</Badge>}
                  </>
                )}
              </li>
            ))}

            {isLoading && (
              <li className="px-4 py-2 flex items-center justify-center text-base-content/50">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm">{t('status.loading')}</span>
              </li>
            )}

            {!isLoading && filteredOptions.length === 0 && (
              <li className="px-4 py-3 text-center text-sm text-base-content/50">
                {t('search.no_results')}
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Mobile: trigger button → in-app bottom sheet with a search box + options. */}
      <button
        type="button"
        id={name ? `${name}-mobile` : undefined}
        disabled={disabled}
        aria-haspopup="listbox"
        onClick={openSheet}
        className={`sm:hidden relative input input-bordered w-full pr-10 text-left flex items-center ${error ? 'input-error' : ''} ${disabled ? 'bg-base-200 cursor-not-allowed' : 'cursor-pointer'} ${isEmpty ? 'text-base-content/40' : ''}`}
      >
        <span className="truncate leading-none">
          {isEmpty ? placeholder : selectedOption?.label || placeholder}
        </span>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/40" />
      </button>
      <BottomSheet isOpen={sheetOpen} onClose={closeSheet} title={label} ariaLabel={label ?? placeholder}>
        <div className="px-2 pb-2 space-y-2">
          <input
            type="text"
            className="input input-bordered w-full"
            placeholder={placeholder}
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            autoComplete="off"
          />
          <ul role="listbox" aria-label={label ?? placeholder}>
            {filteredOptions.map((opt) => (
              <li key={opt.value} role="option" aria-selected={opt.value === value}>
                <button
                  type="button"
                  // aria-label so the option is matchable in the iOS WKWebView a11y
                  // tree (the listbox/option role otherwise hides the inner text).
                  aria-label={opt.label}
                  onClick={() => selectSheetOption(opt)}
                  className={`flex items-center w-full px-4 py-3 rounded-lg text-left hover:bg-base-200 active:bg-base-300 ${
                    opt.value === value ? 'font-semibold text-primary' : ''
                  }`}
                >
                  {opt.description ? (
                    <span className="min-w-0">
                      <span className="flex items-center">
                        <span className="truncate leading-none">{opt.label}</span>
                        {opt.chip && <Badge size="sm" variant="ghost" className="ml-2 shrink-0">{opt.chip}</Badge>}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-base-content/60">{opt.description}</span>
                    </span>
                  ) : (
                    <>
                      <span className="truncate leading-none">{opt.label}</span>
                      {opt.chip && <Badge size="sm" variant="ghost" className="ml-2 shrink-0">{opt.chip}</Badge>}
                    </>
                  )}
                </button>
              </li>
            ))}
            {isLoading && (
              <li className="px-4 py-3 flex items-center justify-center text-base-content/50">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm">{t('status.loading')}</span>
              </li>
            )}
            {!isLoading && filteredOptions.length === 0 && (
              <li className="px-4 py-3 text-center text-sm text-base-content/50">
                {t('search.no_results')}
              </li>
            )}
          </ul>
        </div>
      </BottomSheet>

      {(error || hint) && (
        <label className="label whitespace-normal">
          <span className={`label-text-alt ${error ? 'text-error' : ''}`}>
            {error || hint}
          </span>
        </label>
      )}
    </div>
  )
}
